import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  deckFromOutlineSections,
  inferSourceTitle,
  newId,
  seedDeckContextFromSource,
  type SurfOutlineSection,
  type SurfStyle,
} from "@vida/surf-core";
import { SurfTopbar } from "../components/shell/SurfTopbar";
import { ContentStep } from "../components/wizard/ContentStep";
import { OutlineStep } from "../components/wizard/OutlineStep";
import { StyleStep } from "../components/wizard/StyleStep";
import { useAsyncAction } from "../hooks/useAsyncAction";
import { useSurfLibrary } from "../hooks/useSurfLibrary";
import { useToast } from "../ui/useToast";

type WizardStep = "content" | "outline" | "style";

export function WizardPage() {
  const { deckId } = useParams<{ deckId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { run: runAsync, busy: asyncBusy } = useAsyncAction();
  const { getDeck, saveDeck, ready } = useSurfLibrary();

  const row = deckId ? getDeck(deckId) : undefined;
  const [step, setStep] = useState<WizardStep>("content");
  const [sourceText, setSourceText] = useState("");
  const [presentationType, setPresentationType] = useState(
    row?.deck.theme.presentationType ?? "",
  );
  const [sections, setSections] = useState<SurfOutlineSection[]>([]);
  const [slideTarget, setSlideTarget] = useState<number | "auto">("auto");
  const [draftStyle, setDraftStyle] = useState<SurfStyle | null>(null);
  const [styleConfirmed, setStyleConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);

  const sessionNoteId = useMemo(() => `surf-pdf-${deckId ?? newId()}`, [deckId]);

  if (!deckId) {
    return (
      <div className="library-page">
        <p className="error">Deck mancante.</p>
        <Link to="/">← Libreria</Link>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="library-page">
        <p className="muted">Caricamento…</p>
      </div>
    );
  }

  if (!row) {
    return (
      <div className="library-page">
        <p className="muted">Deck non trovato in cache locale.</p>
        <Link to="/">← Libreria</Link>
      </div>
    );
  }

  const themeStyle = draftStyle ?? row.deck.theme;

  const finishDeck = async () => {
    if (!sections.length) {
      toast.error("Aggiungi almeno una sezione all'outline.");
      return;
    }
    setSaving(true);
    try {
      await runAsync("Generazione deck…", async () => {
        let next = deckFromOutlineSections(
          row.deck,
          sections,
          presentationType || undefined,
        );
        next = seedDeckContextFromSource(next, sourceText, inferSourceTitle(sourceText));
        next.theme = {
          ...themeStyle,
          presentationType: presentationType || themeStyle.presentationType,
        };
        await saveDeck(next, deckId);
        navigate(`/deck/${deckId}`);
      });
    } catch {
      // toast già mostrato da useAsyncAction
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wizard-page wizard-page--fullscreen">
      <SurfTopbar title={row.deck.title} />

      <header className="wizard-header">
        <nav className="wizard-steps" aria-label="Passi wizard">
          <button
            type="button"
            className={step === "content" ? "step active" : "step"}
            onClick={() => setStep("content")}
          >
            1. Contenuto
          </button>
          <button
            type="button"
            className={step === "outline" ? "step active" : "step"}
            onClick={() => setStep("outline")}
            disabled={!sourceText.trim()}
          >
            2. Outline
          </button>
          <button
            type="button"
            className={step === "style" ? "step active" : "step"}
            onClick={() => setStep("style")}
            disabled={!sourceText.trim() || sections.length === 0}
          >
            3. Stile
          </button>
        </nav>
      </header>

      {step === "content" && (
        <>
          <ContentStep
            sourceText={sourceText}
            onSourceChange={setSourceText}
            sessionNoteId={sessionNoteId}
          />
          <div className="wizard-footer">
            <button
              type="button"
              className="primary-btn"
              disabled={!sourceText.trim()}
              onClick={() => setStep("outline")}
            >
              Avanti → Outline
            </button>
          </div>
        </>
      )}

      {step === "outline" && (
        <>
          <OutlineStep
            sourceText={sourceText}
            sections={sections}
            presentationType={presentationType}
            slideTarget={slideTarget}
            onSectionsChange={setSections}
            onPresentationTypeChange={setPresentationType}
            onSlideTargetChange={setSlideTarget}
          />
          <div className="wizard-footer">
            <button type="button" onClick={() => setStep("content")}>
              ← Contenuto
            </button>
            <button
              type="button"
              className="primary-btn"
              disabled={sections.length === 0}
              onClick={() => setStep("style")}
            >
              Avanti → Stile
            </button>
          </div>
        </>
      )}

      {step === "style" && (
        <>
          <StyleStep
            style={themeStyle}
            presentationType={presentationType}
            previewSeed={
              sections[0]?.title?.trim()
                ? { title: sections[0].title, subtitle: sections[0].subtitle }
                : undefined
            }
            onStyleChange={(s) => {
              setDraftStyle(s);
              setStyleConfirmed(false);
            }}
            onStyleConfirmed={setStyleConfirmed}
          />
          <div className="wizard-footer">
            <button type="button" onClick={() => setStep("outline")}>
              ← Outline
            </button>
            {!styleConfirmed && (
              <p className="muted wizard-hint">
                Conferma lo stile con «Usa stile» nell&apos;anteprima prima di generare il deck.
              </p>
            )}
            <button
              type="button"
              className="primary-btn"
              disabled={saving || asyncBusy || sections.length === 0 || !styleConfirmed}
              onClick={() => void finishDeck()}
            >
              Genera deck
            </button>
          </div>
        </>
      )}
    </div>
  );
}
